// One line of a daily brief: what it is, why it is here, what to do.
//
// Shared by Today (everyone's work, grouped by person) and My Day (just
// yours). Kept in one place deliberately — two copies of "how a signal
// reads" would drift, and the wording is doing real work: `basis` is
// the fact that put the item on the list, rendered verbatim so a reader
// can disagree with the brief on the evidence rather than take it on
// faith.
//
// No severity colours. The only threshold in play is the firm's own
// look-ahead setting, and painting rows red would assert a judgment the
// app has not earned. Order carries priority; the numbers speak.

import React from "react";
import { C } from "../constants.js";

// Plain-language labels. The kind strings are for code.
export const KIND_LABELS = {
  deadline_past_due: "Past due",
  deadline_unowned: "Unassigned",
  deadline_due_soon: "Deadline",
  task_past_due: "Task overdue",
  task_due_soon: "Task",
  conflict_unreviewed: "Conflict",
  extraction_pending: "Review",
  care_gap_unexplained: "Treatment gap",
  matter_silent: "Quiet",
};

// How the work was traced to a person. Shown only when it was inferred
// — "assigned" needs no explanation, and labelling it would add noise
// to the majority case we are most confident about.
const ATTRIBUTION_NOTES = {
  responsible_attorney: "via responsible attorney",
  last_touched: "via last activity",
  // Deliberately not annotated: a workflow task names its assignee by
  // id rather than by name, so it is the one attribution in the app
  // that needs no caveat.
};


export function SignalRow({ signal, first, onMatterClick }) {
  const label = KIND_LABELS[signal.kind] || signal.kind;
  const note = ATTRIBUTION_NOTES[signal.attribution];

  return React.createElement("div", {
    className: "stack-row",
    style: {
      display: "flex", gap: "12px", alignItems: "baseline",
      padding: "12px 15px",
      borderTop: first ? "none" : `1px solid ${C.warmGray}55`,
    },
  },
    React.createElement("span", {
      style: { fontSize: "10.5px", fontWeight: 700, color: C.textMuted,
               textTransform: "uppercase", letterSpacing: "0.05em",
               minWidth: "84px", flexShrink: 0 },
    }, label),

    React.createElement("div", { style: { flex: 1, minWidth: 0 } },
      React.createElement("div", {
        style: { fontSize: "13.5px", color: C.textDark, lineHeight: 1.45,
                 fontWeight: signal.is_jurisdictional ? 600 : 400 },
      }, signal.headline),
      React.createElement("div", {
        style: { fontSize: "11.5px", color: C.textMuted, lineHeight: 1.5,
                 marginTop: "2px" },
      }, signal.basis),
      React.createElement("div", {
        style: { fontSize: "11.5px", color: C.textLight, lineHeight: 1.5,
                 marginTop: "2px" },
      }, signal.action),
    ),

    React.createElement("div", {
      style: { flexShrink: 0, textAlign: "right" },
    },
      signal.matter_label && React.createElement("button", {
        onClick: () => onMatterClick && signal.matter_id
          && onMatterClick({ id: Number(signal.matter_id) }),
        style: {
          border: "none", background: "transparent", padding: 0,
          fontSize: "12px", color: C.navy, cursor: "pointer",
          textDecoration: "underline", maxWidth: "170px",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          display: "block",
        },
      }, signal.matter_label),
      note && React.createElement("div", {
        style: { fontSize: "10.5px", color: C.textLight, marginTop: "2px" },
      }, note),
    ),
  );
}
